import { AnimatedNumber } from '../AnimatedNumber';
import { useState, type ReactNode } from 'react';
import { number } from '../arena-ui';
import { CopyGlyph, CrowdGlyph } from './icons';
import type { CoinView } from './model';
import { crowdState, type CrowdSideData } from './crowdModel';
export const money = (value: number | null) =>
  value === null ? '—' : '$' + number(value);
export const percentLabel = (value: number) =>
  (value >= 0 ? '+' : '') + value.toFixed(1) + '%';
export function GraduationStat({
  coin,
  className = 'is-end',
}: {
  coin: CoinView;
  className?: string;
}) {
  const content = (
    <>
      <b>
        <AnimatedNumber value={money(coin.graduationCap)} />
      </b>
      <span>At Graduated</span>
    </>
  );
  return coin.initialPriceHref && !coin.simulated ? (
    <a
      className={className + ' initial-price-link'}
      href={coin.initialPriceHref}
      target="_blank"
      rel="noopener noreferrer"
      title="View pool creation transaction"
      onClick={(event) => event.stopPropagation()}
    >
      {content}
    </a>
  ) : (
    <div className={className}>{content}</div>
  );
}
export function CoinStats({
  coin,
  status,
}: {
  coin: CoinView;
  status: ReactNode;
}) {
  return (
    <div className="coin-stats">
      <div className="coin-stat">
        <b>
          <AnimatedNumber value={money(coin.marketCap)} />
        </b>
        <span>Market Cap{coin.stale && ' · last known'}</span>
      </div>
      {status}
      <GraduationStat coin={coin} className="coin-stat is-end" />
    </div>
  );
}
function CrowdSide({
  count,
  share,
  multiplier,
  label,
  end,
  coefficient,
}: {
  label: string;
  end?: boolean;
  coefficient?: string | null;
} & CrowdSideData) {
  const odds =
    coefficient !== undefined
      ? coefficient === null
        ? '—x'
        : coefficient + 'x'
      : count === 0
        ? '0.0x'
        : multiplier === null
          ? '—x'
          : multiplier.toFixed(1) + 'x';
  const oddsNote =
    coefficient !== undefined
      ? coefficient === null
        ? label + ' coefficient unavailable'
        : 'Current ' + label + ' LMSR coefficient: ' + odds
      : count === 0
        ? 'No participants on ' + label
        : multiplier === null
          ? label + ' crowd multiplier unavailable'
          : 'Current ' + label + ' crowd multiplier: ' + odds;
  const coefficientPill = (
    <span className="crowd-odds" title={oddsNote} aria-label={oddsNote}>
      <AnimatedNumber value={odds} />
    </span>
  );
  const people = (
    <span
      className="crowd-count"
      title={
        count === null
          ? label + ' participants unavailable'
          : count + ' called ' + label
      }
    >
      {!end && coefficientPill}
      {!end && <CrowdGlyph />}
      <AnimatedNumber value={count ?? '—'} />
      {end && <CrowdGlyph />}
      {end && coefficientPill}
      <span className="sr-only">
        {count === null
          ? ' participants unavailable on ' + label
          : ' called ' + label}
      </span>
    </span>
  );
  return (
    <span className={'crowd-side' + (end ? ' is-end' : '')}>
      {end ? (
        <>
          <span>
            <AnimatedNumber
              value={share === null ? '—%' : Number(share.toFixed(1)) + '%'}
            />
          </span>
          <i aria-hidden="true">•</i>
          {people}
        </>
      ) : (
        <>
          {people}
          <i aria-hidden="true">•</i>
          <span>
            <AnimatedNumber
              value={share === null ? '—%' : Number(share.toFixed(1)) + '%'}
            />
          </span>
        </>
      )}
    </span>
  );
}
export function CrowdMeter({ coin }: { coin: CoinView }) {
  const crowdOnly = coin.simulated && !coin.id.startsWith('game-');
  const crowd = crowdState(coin);
  const active = crowd.kind === 'active';
  const note =
    crowd.kind === 'empty'
      ? 'No participants yet.'
      : crowd.kind === 'unavailable'
        ? coin.choice
          ? 'Crowd data unavailable.'
          : 'Crowd data unavailable or hidden until you make a call.'
        : null;
  return (
    <div
      className="crowd-meter"
      data-crowd-state={crowd.kind}
      title={note ?? undefined}
    >
      <CrowdSide
        {...crowd.down}
        label="DIE!"
        coefficient={
          crowdOnly ? undefined : (coin.odds?.coefficients.DIES ?? null)
        }
      />
      <span className="crowd-bar" aria-hidden="true">
        <i
          className="is-down"
          style={{ flexGrow: active ? crowd.down.share! : 1 }}
        />
        <i
          className="is-up"
          style={{ flexGrow: active ? crowd.up.share! : 1 }}
        />
      </span>
      <CrowdSide
        {...crowd.up}
        label="RUN!"
        end
        coefficient={
          crowdOnly ? undefined : (coin.odds?.coefficients.SURVIVES ?? null)
        }
      />
      {note && <span className="sr-only">{note}</span>}
    </div>
  );
}
export function AddressPill({
  address,
  simulated = false,
}: {
  address: string;
  simulated?: boolean;
}) {
  const [copiedAddress, setCopiedAddress] = useState<string | null>(null);
  const copied = copiedAddress === address;
  return (
    <button
      type="button"
      className="coin-address"
      title={(simulated ? 'Test-only address: ' : '') + address}
      aria-label={
        (copied ? 'Copied ' : 'Copy ') +
        (simulated ? 'test contract address' : 'contract address')
      }
      onClick={() => {
        void navigator.clipboard
          ?.writeText(address)
          .then(() => setCopiedAddress(address))
          .catch(() => setCopiedAddress(null));
      }}
      onMouseLeave={() => setCopiedAddress(null)}
    >
      <CopyGlyph size={12} />
      <span>{copied ? 'Copied' : address}</span>
    </button>
  );
}
